import {
  coerceWhatMembersNeedFulfillmentStats,
  coerceWhatMembersNeedIdentifiedVsFulfilled,
  coerceWhatMembersNeedTopNeeds,
  normalizeWhatMembersNeedSnapshot,
  WHAT_MEMBERS_NEED_FULFILLMENT_STEPS,
  buildWhatMembersNeedTotalFulfillmentSubcopy,
  buildWhatMembersNeedTopNeedsSubcopy,
  type WhatMembersNeedFulfillmentStep,
} from '@act/shared/data-transfer-objects';
import SectionReveal from './SectionReveal';
import SectionBanner from './SectionBanner';
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import { useReportsPrintStaticLayout } from '../hooks/useReportsPrintStaticLayout.hook';
import { ReportMockFieldFrame } from '../ReportMockFieldFrame';
import type { ReportHostedSectionProps } from '../reportHostedSection.types';
import { BrandLineChart } from './BrandChart';
import { WhatMembersNeedTopNeedsChart } from './WhatMembersNeedTopNeedsChart';
import {
  body,
  border,
  CORAL,
  display,
  foreground,
  muted,
  NAVY,
} from './tokens';

const cardStyle = {
  background: '#fff',
  border: `1px solid ${border}`,
} as const;

/**
 * Renders one numbered fulfillment journey step with optional arrow separators.
 * @param step - Step metadata from snapshot constants.
 * @param showArrow - Whether to show a leading arrow separator.
 * @param showTrailingArrow - Whether to show a trailing arrow separator.
 */
const FulfillmentStep = ({
  step,
  showArrow,
  showTrailingArrow = false,
}: {
  step: WhatMembersNeedFulfillmentStep;
  showArrow: boolean;
  showTrailingArrow?: boolean;
}) => (
  <span className="inline-flex items-center gap-2">
    {showArrow ? (
      <span style={{ color: CORAL, opacity: 0.5 }}>→</span>
    ) : null}
    <span className="inline-flex items-center gap-2">
      <span
        className="text-xs font-semibold tabular-nums"
        style={{ color: CORAL }}
      >
        {String(step.step).padStart(2, '0')}
      </span>
      {step.title}
    </span>
    {showTrailingArrow ? (
      <span style={{ color: CORAL, opacity: 0.5 }}>→</span>
    ) : null}
  </span>
);

/**
 * What Members Need section: fulfillment stats, ranked needs, and identified vs fulfilled trend.
 * @param props - Resolved snapshot from warehouse or mocks.
 */
const WhatMembersNeed = ({
  data,
  customer,
  reportingPeriod,
}: ReportHostedSectionProps) => {
  const printStatic = useReportsPrintStaticLayout();
  const displayData = normalizeWhatMembersNeedSnapshot(
    data,
    customer,
    reportingPeriod
  );

  const topNeeds = coerceWhatMembersNeedTopNeeds(displayData.topNeeds);
  const trend = coerceWhatMembersNeedIdentifiedVsFulfilled(
    displayData.identifiedVsFulfilled
  );
  const fulfillmentStats = coerceWhatMembersNeedFulfillmentStats(
    displayData.fulfillmentStats
  );
  const leadNeed = topNeeds[0];
  const leadNeedTotal = topNeeds.reduce((sum, row) => sum + row.count, 0);
  const leadNeedPct =
    leadNeed?.pct ??
    (leadNeed && leadNeedTotal > 0
      ? Math.round((leadNeed.count / leadNeedTotal) * 100)
      : 0);
  const trendChartData = trend.map(row => ({
    label: row.label,
    identified: row.identified,
    fulfilled: row.fulfilled,
  }));

  return (
    <HostedReportSectionLayout
      sectionDomId="what-members-need"
      className="section--white report-hosted-section-body"
      style={{ fontFamily: body }}
      banner={
        <SectionBanner
          title="What Members Need"
          iconHref="#icon_community_guide"
        />
      }
    >
      <SectionReveal>
        {fulfillmentStats ? (
          <ReportMockFieldFrame
            data={displayData}
            field="fulfillmentStats"
            className="mb-10"
          >
            <div className="what-members-need-stats-grid grid md:grid-cols-2 gap-4">
              <div
                className="rounded-2xl p-6 text-center shadow-sm"
                style={cardStyle}
              >
                <div
                  className="stat-num stat-num--md tabular-nums"
                  style={{ color: foreground, fontFamily: display }}
                >
                  {fulfillmentStats.monthlyFulfilledCount.toLocaleString()}
                </div>
                <div
                  className="text-xs font-semibold uppercase tracking-[0.14em] mt-2"
                  style={{ color: foreground }}
                >
                  Needs fulfilled this month
                </div>
              </div>
              <div
                className="rounded-2xl p-6 text-center shadow-sm"
                style={cardStyle}
              >
                <div
                  className="stat-num stat-num--md tabular-nums"
                  style={{ color: foreground, fontFamily: display }}
                >
                  {fulfillmentStats.totalFulfillmentPct}%
                </div>
                <div
                  className="text-xs font-semibold uppercase tracking-[0.14em] mt-2"
                  style={{ color: foreground }}
                >
                  Total needs fulfilled
                </div>
                <div className="text-sm mt-1" style={{ color: muted }}>
                  {buildWhatMembersNeedTotalFulfillmentSubcopy(
                    fulfillmentStats.totalFulfilledCount,
                    fulfillmentStats.totalIdentifiedCount,
                  )}
                </div>
              </div>
            </div>
          </ReportMockFieldFrame>
        ) : null}

        <div className="what-members-need-fulfillment-steps mb-10 text-sm py-4" style={{ color: muted }}>
          <div className="flex flex-wrap justify-center items-center gap-2 mb-2">
            {WHAT_MEMBERS_NEED_FULFILLMENT_STEPS.slice(0, 3).map(
              (step, index) => (
                <FulfillmentStep
                  key={step.step}
                  step={step}
                  showArrow={index > 0}
                  showTrailingArrow={index === 2}
                />
              ),
            )}
          </div>
          <div className="flex justify-center">
            <FulfillmentStep
              step={WHAT_MEMBERS_NEED_FULFILLMENT_STEPS[3]}
              showArrow={false}
            />
          </div>
        </div>
      </SectionReveal>

      <SectionReveal delay={0.05}>
        <div className="what-members-need-charts-grid grid md:grid-cols-2 gap-6">
          <ReportMockFieldFrame data={displayData} field="topNeeds">
            <div className="rounded-2xl p-6 shadow-sm h-full" style={cardStyle}>
              <h3
                className="text-base font-semibold mb-1"
                style={{ color: foreground, fontFamily: "'Lora', serif" }}
              >
                What members need most
              </h3>
              {leadNeed ? (
                <p className="text-sm mb-8" style={{ color: muted }}>
                  {buildWhatMembersNeedTopNeedsSubcopy(
                    leadNeed.label,
                    leadNeedPct,
                  )}
                </p>
              ) : null}
              {topNeeds.length === 0 ? (
                <p className="text-sm" style={{ color: muted }}>
                  No need data for this reporting period yet. Use Refresh data
                  in the builder to reload warehouse snapshots.
                </p>
              ) : (
                <WhatMembersNeedTopNeedsChart
                  rows={topNeeds}
                  printStatic={printStatic}
                />
              )}
            </div>
          </ReportMockFieldFrame>

          <ReportMockFieldFrame
            data={displayData}
            field="identifiedVsFulfilled"
          >
            <div className="rounded-2xl p-6 shadow-sm h-full" style={cardStyle}>
              <h3
                className="text-base font-semibold mb-1"
                style={{ color: foreground, fontFamily: "'Lora', serif" }}
              >
                Needs: Identified vs. Fulfilled
              </h3>
              <p className="text-sm mb-5" style={{ color: muted }}>
                Fulfillment climbs as the guide network deepens.
              </p>
              {trend.length === 0 ? (
                <p className="text-sm" style={{ color: muted }}>
                  No trend data for this period.
                </p>
              ) : (
                <div className="what-members-need-trend-chart">
                <BrandLineChart
                  data={trendChartData}
                  series={[
                    { key: 'identified', color: NAVY, label: 'Identified' },
                    { key: 'fulfilled', color: CORAL, label: 'Fulfilled' },
                  ]}
                  height={printStatic ? 168 : 320}
                  margin={
                    printStatic
                      ? { top: 8, right: 16, bottom: 18, left: 4 }
                      : { top: 12, right: 20, bottom: 24, left: 8 }
                  }
                  yAxisWidth={printStatic ? 44 : 52}
                />
                </div>
              )}
            </div>
          </ReportMockFieldFrame>
        </div>
      </SectionReveal>
    </HostedReportSectionLayout>
  );
};

export default WhatMembersNeed;
